// LAYOUT-SPEC §0 — Record review
//
//   ONE QUESTION  What did the government reviewer decide on this submission?
//   PRIMARY OBJECT  One review of one submission (Archetype C).
//   PRIMARY VERB    Save. Gated on area:execution.verdict (D111): recording
//                 the verdict is granted apart from doing the work.
//
// The decided-on date is never pre-filled (D98): the reviewer states it, or
// accepts the "Use today's date" suggestion.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDateField, SpecraFormField, SpecraInput, SpecraSegmentedControl, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useCmDrawerParams } from '../drawerParams';
import { useOccurrence, useRecordReview } from '../hooks';
import { cmDate } from '../lib/format';
import { REVIEW_TIERS, tierLabel } from '../lib/status';
import { useCmLookups } from '../lookups';
import { CmDrawer } from './CmDrawer';
import { UseTodayButton } from './OccurrenceDrawer';
const DECISIONS = [
    {
        value: 'approved',
        label: 'Approve'
    },
    {
        value: 'rejected',
        label: 'Reject'
    },
    {
        value: 'comments',
        label: 'Comments'
    }
];
export function RecordReviewDrawer({ programId, occId, submissionId }) {
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const occ = useOccurrence(programId, occId);
    const submission = occ.data?.submissions.find((s)=>s.id === submissionId);
    const duty = occ.data?.occurrence ? lk.dutyById.get(occ.data.occurrence.dutyId) : undefined;
    const [tierPick, setTierPick] = useState(null);
    const tier = tierPick ?? duty?.finalReviewTier ?? 'va_pm';
    const [decision, setDecision] = useState('approved');
    const [reviewer, setReviewer] = useState('');
    const [decidedOn, setDecidedOn] = useState('');
    const [notes, setNotes] = useState('');
    const [receipt, setReceipt] = useState(null);
    const record = useRecordReview(programId, {
        errors: 'inline'
    });
    const notesMissing = decision === 'rejected' && notes.trim() === '';
    const canSave = !!submission && decidedOn !== '' && !notesMissing && !record.isPending;
    const close = ()=>params.close('review');
    const save = ()=>record.mutate({
            submissionId,
            tier,
            decision,
            reviewerDisplay: reviewer.trim(),
            decidedOn,
            notes: notes.trim()
        }, {
            onSuccess: (r)=>{
                if (r.converged) {
                    setReceipt('Converged: this restates the live review, so nothing new was recorded.');
                    return;
                }
                const replaced = submission?.reviews.find((x)=>x.id === r.supersededReviewId);
                toast.success(replaced ? `Review recorded. Replaces ${replaced.reviewerDisplay || 'the'}${replaced.reviewerDisplay ? '’s' : ''} ${tierLabel(replaced.tier)} review of ${cmDate(replaced.decidedOn)}.` : 'Review recorded');
                close();
            }
        });
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Record review",
        description: submission ? `${duty?.title ?? 'Deliverable'} · submission #${submission.attempt} of ${cmDate(submission.submittedOn, 'an unstated day')}` : undefined,
        onClose: close,
        busy: record.isPending,
        testId: Testids.cmReview.drawer,
        footerProps: {
            confirmLabel: 'Save review',
            onConfirm: save,
            confirmDisabled: !canSave,
            loading: record.isPending,
            onCancel: close,
            confirmProps: {
                'data-testid': Testids.cmReview.save
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Reviewer tier",
                    required: true,
                    hint: `This deliverable's final tier is ${tierLabel(duty?.finalReviewTier)}.`,
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        value: tier,
                        onChange: (e)=>setTierPick(e.target.value),
                        "data-testid": Testids.cmReview.tier,
                        children: REVIEW_TIERS.map((t)=>/*#__PURE__*/ _jsx("option", {
                                value: t,
                                children: tierLabel(t)
                            }, t))
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Decision",
                    value: decision,
                    options: DECISIONS,
                    onValueChange: setDecision,
                    testId: Testids.cmReview.decision
                }),
                /*#__PURE__*/ _jsxs(SpecraFormField, {
                    label: "Reviewer",
                    hint: "The person at that tier, e.g. “Kary Storms”.",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            value: reviewer,
                            onChange: (e)=>setReviewer(e.target.value),
                            list: "cm-review-contacts",
                            "data-testid": Testids.cmReview.reviewer
                        }),
                        /*#__PURE__*/ _jsx("datalist", {
                            id: "cm-review-contacts",
                            children: lk.contacts.map((c)=>/*#__PURE__*/ _jsx("option", {
                                    value: c.name
                                }, c.id))
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Decided on",
                    required: true,
                    value: decidedOn,
                    onValueChange: setDecidedOn,
                    hint: /*#__PURE__*/ _jsx(UseTodayButton, {
                        onUse: setDecidedOn,
                        testId: Testids.cmReview.useToday
                    }),
                    "data-testid": Testids.cmReview.decidedOn
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Notes",
                    required: decision === 'rejected',
                    error: notesMissing ? 'Say what the reviewer asked to change.' : undefined,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 3,
                        value: notes,
                        onChange: (e)=>setNotes(e.target.value),
                        "data-testid": Testids.cmReview.notes
                    })
                }),
                receipt ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "muted",
                    "data-testid": Testids.cmReview.converged,
                    children: receipt
                }) : null,
                record.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    "data-testid": Testids.cmReview.error,
                    children: apiErrorMessage(record.error, 'Could not record the review')
                }) : null
            ]
        })
    });
}
